import { getLocale, t, useI18n } from '../../i18n';
import React, { useEffect, useState } from 'react';
import { reportService } from '../../services/reportService';
import type {
  ReportListItem,
  ReportListSort,
  ReportStatusCounts,
} from '../../services/reportService';
import {
  getReportPreview,
  getReportTitle,
  reportTypeLabelFor,
} from '../../utils/reportPresentation';
import './ReportsPage.css';

export type ReportsCategory = 'incident' | 'summary';

interface FilterOption {
  value: string;
  label: string;
}

const PAGE_SIZE = 50;
const EMPTY_STATUS_COUNTS: ReportStatusCounts = {
  open: 0,
  submitted: 0,
  in_review: 0,
  needs_more_info: 0,
  escalated: 0,
  closed: 0,
};

function applyStatusFilterToCounts(
  counts: ReportStatusCounts,
  status: string,
): ReportStatusCounts {
  if (!status) return counts;
  if (status === 'open') return { ...counts, closed: 0 };
  if (!(status in counts) || status === 'open') return counts;

  const statusKey = status as keyof Omit<ReportStatusCounts, 'open'>;
  const matchingCount = counts[statusKey];
  return {
    ...EMPTY_STATUS_COUNTS,
    open: status === 'closed' ? 0 : matchingCount,
    [statusKey]: matchingCount,
  };
}

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'open', label: 'Open' },
  { value: '', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_review', label: 'In Review' },
  { value: 'needs_more_info', label: 'Needs More Info' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS: FilterOption[] = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const TYPE_OPTIONS: FilterOption[] = [
  { value: '', label: 'All Types' },
  { value: 'incident', label: 'Incident' },
  { value: 'use_of_force', label: 'Use of Force' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'alarm', label: 'Alarm' },
  { value: 'general', label: 'General' },
];

const SUMMARY_TYPE_OPTIONS: FilterOption[] = [
  { value: '', label: 'All Summary Types' },
  { value: 'daily_activity', label: 'Daily Activity' },
  { value: 'patrol', label: 'Patrol' },
  { value: 'access_visitor', label: 'Visitor Log' },
];

const SORT_OPTIONS: FilterOption[] = [
  { value: 'created_desc', label: 'Created (Newest)' },
  { value: 'created_asc', label: 'Created (Oldest)' },
  { value: 'updated_desc', label: 'Updated (Newest)' },
  { value: 'updated_asc', label: 'Updated (Oldest)' },
];

const getPriorityBadgeClass = (priority: string | null | undefined): string => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'badge badge--critical';
    case 'high': return 'badge badge--high';
    case 'medium': return 'badge badge--medium';
    case 'low': return 'badge badge--low';
    default: return 'badge badge--default';
  }
};

const getStatusBadgeClass = (status: string | null | undefined): string => {
  switch (status) {
    case 'submitted': return 'badge badge--status-submitted';
    case 'in_review': return 'badge badge--status-in-review';
    case 'needs_more_info': return 'badge badge--status-needs-info';
    case 'escalated': return 'badge badge--status-escalated';
    case 'closed': return 'badge badge--status-closed';
    default: return 'badge badge--default';
  }
};

const getStatusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case 'submitted': return t('Submitted');
    case 'in_review': return t('In Review');
    case 'needs_more_info': return t('Needs More Info');
    case 'escalated': return t('Escalated');
    case 'closed': return t('Closed');
    default: return status || t('Unknown');
  }
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return t('N/A');
  return new Date(dateStr).toLocaleString(getLocale());
};

const formatRelativeDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (diffMins < 1) return t('just now');
  if (diffMins < 60) return t('{count}m ago', { count: diffMins });
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t('{count}h ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t('{count}d ago', { count: diffDays });
  return date.toLocaleDateString(getLocale());
};

interface Props {
  onOpenReport?: (reportId: string) => void;
  category?: ReportsCategory;
}

export default function ReportsPage({
  onOpenReport,
  category = 'incident',
}: Props) {
  useI18n();
  const [companyId, setCompanyId] = useState('');
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<ReportStatusCounts>(EMPTY_STATUS_COUNTS);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('open');
  const [selectedType, setSelectedType] = useState('');
  const [sortBy, setSortBy] = useState<ReportListSort>('created_desc');
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      try {
        const profile = await reportService.getCurrentUserProfile();
        if (!cancelled) setCompanyId(profile.company_id);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading report profile:', (err as Error).message);
          setErrorMessage(t('Unable to load reports. Please try again.'));
          setIsLoading(false);
          setHasLoaded(true);
        }
      }
    };
    void loadProfile();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedType('');
    setPage(0);
  }, [category]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    const loadReports = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const result = await reportService.fetchReports({
          companyId,
          page,
          pageSize: PAGE_SIZE,
          category,
          search: debouncedSearch,
          priority: selectedPriority,
          status: selectedStatus,
          reportType: selectedType,
          sortBy,
        });
        if (cancelled) return;

        const lastPage = Math.max(0, Math.ceil(result.totalCount / PAGE_SIZE) - 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setReports(result.reports);
        setTotalCount(result.totalCount);
        setStatusCounts(result.statusCounts);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading reports:', (err as Error).message);
          setErrorMessage(t('Unable to load reports. Please try again.'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setHasLoaded(true);
        }
      }
    };

    void loadReports();
    return () => { cancelled = true; };
  }, [
    category,
    companyId,
    debouncedSearch,
    page,
    refreshToken,
    selectedPriority,
    selectedStatus,
    selectedType,
    sortBy,
  ]);

  const isSummaryCategory = category === 'summary';
  const typeOptions = isSummaryCategory ? SUMMARY_TYPE_OPTIONS : TYPE_OPTIONS;
  const pageTitle = isSummaryCategory ? 'Summary Reports' : 'Incident Reports';
  const pageEyebrow = isSummaryCategory ? 'Shift summaries' : 'Incident management';
  const pageSubtitle = isSummaryCategory
    ? 'Review daily shift summaries, patrol reports and visitor logs separately from incident investigations.'
    : 'Prioritise active reports, filter noise quickly and keep investigations moving without losing context.';
  const emptyCopy = isSummaryCategory
    ? 'Adjust the filters or refresh to pull the latest summary data.'
    : 'Adjust the filters or refresh to pull the latest incident data.';
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const firstShown = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastShown = Math.min((page + 1) * PAGE_SIZE, totalCount);
  const displayedStatusCounts = applyStatusFilterToCounts(statusCounts, selectedStatus);

  if (!hasLoaded && isLoading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <div className="page reports-page" aria-busy={isLoading}>
      <div className="page__header">
        <div>
          <p className="page__eyebrow">{pageEyebrow}</p>
          <h1 className="page__title">{pageTitle}</h1>
          <p className="page__subtitle">{pageSubtitle}</p>
        </div>
        <div className="page__header-actions">
          <button
            onClick={() => setRefreshToken((value) => value + 1)}
            className="btn btn--secondary"
            disabled={isLoading}
          >
            {isLoading ? t('Loading...') : t('Refresh')}
          </button>
        </div>
      </div>

      <p className="report-stats__scope">{t('Counts across all matching reports')}</p>
      <div className="report-stats">
        <div className="stat-card">
          <div className="stat-card__value stat-card__value--error">{displayedStatusCounts.open}</div>
          <div className="stat-card__label">{t('Open')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value" style={{ color: 'var(--color-warning)' }}>{displayedStatusCounts.submitted}</div>
          <div className="stat-card__label">{t('Submitted')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value" style={{ color: '#3B82F6' }}>{displayedStatusCounts.in_review}</div>
          <div className="stat-card__label">{t('In Review')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value" style={{ color: '#A855F7' }}>{displayedStatusCounts.needs_more_info}</div>
          <div className="stat-card__label">{t('Needs Info')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value stat-card__value--error">{displayedStatusCounts.escalated}</div>
          <div className="stat-card__label">{t('Escalated')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__value stat-card__value--success">{displayedStatusCounts.closed}</div>
          <div className="stat-card__label">{t('Closed')}</div>
        </div>
      </div>

      <div className="reports-filters panel">
        <input
          type="search"
          placeholder={t('Search reports, guards or sites')}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="form__input reports-filters__search"
        />
        <select value={selectedStatus} onChange={(event) => { setSelectedStatus(event.target.value); setPage(0); }} className="form__select reports-filters__select">
          {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select value={selectedPriority} onChange={(event) => { setSelectedPriority(event.target.value); setPage(0); }} className="form__select reports-filters__select">
          {PRIORITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select value={selectedType} onChange={(event) => { setSelectedType(event.target.value); setPage(0); }} className="form__select reports-filters__select">
          {typeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select value={sortBy} onChange={(event) => { setSortBy(event.target.value as ReportListSort); setPage(0); }} className="form__select reports-filters__select">
          {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      <div className="reports-table-shell">
        <div className="reports-table-shell__header">
          <p className="reports-showing-count">
            {isLoading ? t('Loading...') : `Showing ${firstShown}-${lastShown} of ${totalCount} reports`}
          </p>
        </div>

        {errorMessage ? (
          <div className="empty-state">
            <div className="empty-state__title">{errorMessage}</div>
            <button className="btn btn--secondary" onClick={() => setRefreshToken((value) => value + 1)}>{t('Try again')}</button>
          </div>
        ) : reports.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">{t('No reports found')}</div>
            <div className="empty-state__sub">{emptyCopy}</div>
          </div>
        ) : (
          <div className={`reports-table-shell__scroller${isLoading ? ' reports-table-shell__scroller--loading' : ''}`}>
            <table className="table report-table">
              <thead><tr>
                <th>{t('Report')}</th><th>{t('Type')}</th><th>{t('Guard')}</th><th>{t('Site')}</th><th>{t('Assigned To')}</th><th>{t('Status')}</th><th>{t('Priority')}</th><th>{t('Created')}</th><th>{t('Updated')}</th>
              </tr></thead>
              <tbody>
                {reports.map((report) => {
                  const profile = report.profiles as { full_name?: string | null } | null;
                  const site = report.sites as { name?: string | null } | null;
                  const assignee = report.assignee as { full_name?: string | null } | null;
                  return (
                    <tr key={report.id} className="report-table__row" onClick={() => onOpenReport?.(report.id)}>
                      <td><div className="report-table__title">{getReportTitle(report)}</div><div className="report-table__preview">{getReportPreview(report)}</div></td>
                      <td><span className="report-table__type">{reportTypeLabelFor(report.report_type)}</span></td>
                      <td>{profile?.full_name || t('Unknown')}</td>
                      <td>{site?.name || t('Unknown')}</td>
                      <td>{assignee?.full_name ? <span className="text-label">{assignee.full_name}</span> : <span className="text-muted">{t('Unassigned')}</span>}</td>
                      <td><span className={getStatusBadgeClass(report.status)}>{getStatusLabel(report.status)}</span></td>
                      <td><span className={getPriorityBadgeClass(report.priority)}>{report.priority || t('N/A')}</span></td>
                      <td><span className="text-muted" title={formatDate(report.created_at)}>{formatRelativeDate(report.created_at)}</span></td>
                      <td><span className="text-muted" title={formatDate(report.status_updated_at)}>{formatRelativeDate(report.status_updated_at)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!errorMessage && totalCount > 0 && (
          <div className="reports-pagination">
            <button className="btn btn--secondary" onClick={() => setPage((value) => Math.max(0, value - 1))} disabled={page === 0 || isLoading}>{t('Previous')}</button>
            <span className="reports-pagination__label">Page {page + 1} of {pageCount}</span>
            <button className="btn btn--secondary" onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))} disabled={page + 1 >= pageCount || isLoading}>{t('Next')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
